import * as React from 'react';
import * as _ from 'lodash';
import {
  VtxInput,
  VtxDayPicker,
  VtxSelect,
  VtxCheckbox,
  VtxInputTextArea,
  VtxDayRangePicker,
  VtxRadioGroup,
  VtxRadio,
  VtxOption,
  VtxMultiSelect,
} from '@vertexinc/vtx-ui-react-component-library';
import * as JSJoda from '@js-joda/core';
import Logging from '../components/Logging/Logging';
import { formatDateISO } from '../components/Formatting/DateTime';
import { isString } from '../components/Parsing/String';
import { validateEmail } from '../components/Validation/Email';
import { validatePhone, validatePhoneExtension } from '../components/Validation/Phone';
import { validatePostalCode } from '../components/Validation/PostalCode';
import './Controls.scss';
import '../styles/common.scss';

import { formatDecimal, jsJodaLocalDateToJavascriptDate, utcToLocal } from '..';
import { validateIllegalCharachters } from '../components/Validation/IllegalCharacters';

export interface SearchAhead {
  searching: boolean;
  onSearch: (searchValue: string) => void;
  searchingText: string;
  noResultsText: string;
  debounceMilliseconds: number;
}

export interface ControlState {
  editable: boolean;
  readOnly: boolean;
  disabled: boolean;
}

export interface ControlStyleClass {
  fieldHeader: string;
  fieldBase: string;
  fieldEnabled: string;
  fieldDisabled: string;
  fieldValidation?: string;
  fieldDropDown?: string; // The Drop List Style only
}

export interface ControlValidationField {
  field: string;
  message: string;
  type?: 'Error' | 'Warning' | 'Success' | 'Information';
}

export interface ControlValidationValueField {
  field: string;
  minValue?: number;
  maxValue?: number;
}

export interface ControlValidationSizeField {
  field: string;
  minSize?: number;
  maxSize?: number;
}

export interface ControlValidationIllegalCharacterField {
  field: string;
  illegalCharacters: string[];
}

export interface ControlValidation {
  requiredFields?: string[];
  minMaxSize?: ControlValidationSizeField[];
  minMaxValue?: ControlValidationValueField[];
  illegalCharacters?: ControlValidationIllegalCharacterField[];
  emailFields?: string[];
  phoneFields?: string[];
  phoneExtensionFields?: string[];
  postalCodeFields?: string[];
  customMessages?: ControlValidationField[];
  country?: string;
  hideEmptyValidation?: boolean;
}

export interface DisplayData {
  display: string;
  data: string;
}

export interface RadioOption {
  display: string;
  data: number;
}

// Default Style
const defaultControlStyle: ControlStyleClass = {
  fieldHeader: 'defaultFieldHeader',
  fieldBase: 'defaultFieldBase',
  fieldEnabled: 'defaultFieldEnabled',
  fieldDisabled: 'defaultFieldDisabled',
  fieldValidation: 'defaultFieldValidation',
  fieldDropDown: null,
};

function updateArray(index: number, value: boolean, values: boolean[]): boolean[] {
  const copyValues = [...values];
  copyValues[index] = value;
  return copyValues;
}

// Checkbox Value has Changed
function onChangeCheckboxMultiple(
  event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  index: number,
  onChangeValue?: React.Dispatch<React.SetStateAction<boolean[]>>,
): void {
  if (onChangeValue) {
    const value: boolean = event.target.checked;

    Logging.Debug('onChangeCheckboxMultiple', event.target.id, index, value);
    onChangeValue((previousArray) => updateArray(index, value, previousArray));
  }
}

export function renderFieldValidation(
  id: string,
  value: any, // eslint-disable-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
  controlState: ControlState,
  controlValidation: ControlValidation,
  controlStyle: ControlStyleClass,
  multiple = false,
): JSX.Element {
  if (!controlValidation || !controlState.editable) {
    return (
      <></>
    );
  }

  let validationMessage = null;
  let additionalStyle = '';

  // Required Fields
  if (controlValidation.requiredFields && (controlValidation.requiredFields.indexOf(id) !== -1)) {
    if ((value === null) || (value === undefined) || (value === '')) {
      validationMessage = 'This is a required field';
    }

    if (multiple && value?.length === 0) {
      validationMessage = 'This is a required field';
    }
  }

  // Email Fields
  if (controlValidation.emailFields && (controlValidation.emailFields.indexOf(id) !== -1)) {
    if (!validateEmail(value)) {
      validationMessage = 'Invalid E-Mail address entered';
    }
  }

  // Phone Fields
  if (controlValidation.phoneFields && (controlValidation.phoneFields.indexOf(id) !== -1)) {
    if (!validatePhone(value)) {
      validationMessage = 'Invalid Phone/Fax Number entered';
    }
  }

  // Phone Extension Fields
  if (controlValidation.phoneExtensionFields && (controlValidation.phoneExtensionFields.indexOf(id) !== -1)) {
    if (!validatePhoneExtension(value)) {
      validationMessage = 'Invalid Extension.  Only numbers allowed';
    }
  }

  // Postal Code Fields
  if (controlValidation.postalCodeFields && (controlValidation.postalCodeFields.indexOf(id) !== -1)) {
    if (!validatePostalCode(value, controlValidation.country)) {
      validationMessage = 'Invalid Postal / Zip Code entered';
    }
  }

  if (controlValidation.illegalCharacters) {
    const field: ControlValidationIllegalCharacterField = controlValidation.illegalCharacters.filter((item) => item.field === id)[0];

    if (field && field.illegalCharacters) {
      if (!validateIllegalCharachters(value, field.illegalCharacters)) {
        validationMessage = field.illegalCharacters.length > 1
          ? `The ${field.illegalCharacters.join(' ')} characters are not allowed`
          : `The ${field.illegalCharacters.join(' ')} character is not allowed`;
      }
    }
  }

  if (controlValidation.minMaxSize) {
    const field: ControlValidationSizeField = controlValidation.minMaxSize.filter((item) => item.field === id)[0];

    if (field && field.minSize) {
      if (value?.length < field.minSize) {
        validationMessage = `A min of ${field.minSize} characters are allowed`;
      }
    }

    if (field && field.maxSize) {
      if (value?.length > field.maxSize) {
        validationMessage = `A max of ${field.maxSize} characters are allowed`;
      }
    }
  }

  if (controlValidation.minMaxValue) {
    if ((value === null) || (value === undefined) || (value === '')) {
      // Nothing
    } else {
      const field: ControlValidationValueField = controlValidation.minMaxValue.filter((item) => item.field === id)[0];

      if (field && (field.minValue !== null)) {
        if (Number(value) < field.minValue) {
          validationMessage = `A min value of ${formatDecimal(field.minValue)} is allowed`;
        }
      }

      if (field && (field.maxValue !== null)) {
        if ((Number(value) > field.maxValue)) {
          validationMessage = `A max value of ${formatDecimal(field.maxValue)} is allowed`;
        }
      }
    }
  }

  if (controlValidation.customMessages) {
    const field: ControlValidationField = controlValidation.customMessages.filter((item) => item.field === id)[0];

    if (field) {
      validationMessage = field.message;
      switch (field.type) {
        case 'Error':
          additionalStyle = 'validationError';
          break;
        case 'Warning':
          additionalStyle = 'validationWarning';
          break;
        case 'Success':
          additionalStyle = 'validationSuccess';
          break;
        case 'Information':
          additionalStyle = 'validationInformation';
          break;
        default:
          break;
      }
    }
  }

  // Hide the validation Message if Needed
  if (controlValidation.hideEmptyValidation && !validationMessage) {
    return (
      <></>
    );
  }

  return (
    <span id={`${id}-Validation`} className={`${controlStyle.fieldValidation} ${additionalStyle}`}>{validationMessage}</span>
  );
}

// Checkbox Value has Changed
function onChangeCheckbox(
  event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  onChangeValue?: React.Dispatch<React.SetStateAction<boolean>>,
): void {
  if (onChangeValue) {
    const value: boolean = event.target.checked;
    Logging.Debug('onChangeCheckbox', event.target.id, value);
    onChangeValue(value);
  }
}

// Render a Checkbox Field
export function renderCheckbox(
  id: string,
  heading: string,
  value: boolean,
  index: number,
  onChangeValue: React.Dispatch<React.SetStateAction<boolean>> | React.Dispatch<React.SetStateAction<boolean[]>>,
  controlState: ControlState,
  cssClass?: string,
): JSX.Element {
  const classNames = `${cssClass}`;

  return (
    <div className={classNames} key={id}>
      <VtxCheckbox
        id={id}
        name={id}
        checked={value}
        disabled={controlState.disabled}
        onChange={(event): void => (index !== null) // eslint-disable-line no-confusing-arrow
          ? onChangeCheckboxMultiple(event, index, onChangeValue as React.Dispatch<React.SetStateAction<boolean[]>>)
          : onChangeCheckbox(event, onChangeValue as React.Dispatch<React.SetStateAction<boolean>>)}
      >
        {heading}
      </VtxCheckbox>
    </div>
  );
}

// Render a Checkbox Field with Heading
export function renderCheckboxWithHeading(
  size: number,
  id: string,
  heading: string,
  description: string,
  value: boolean,
  onChangeValue: React.Dispatch<React.SetStateAction<boolean>> | React.Dispatch<React.SetStateAction<boolean[]>>,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
  controlValidation: ControlValidation = null,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {
          heading
          && (
            <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
          )
        }
        <VtxCheckbox
          id={id}
          name={id}
          checked={value}
          disabled={controlState.disabled}
          onChange={(event): void => onChangeCheckbox(event, onChangeValue as React.Dispatch<React.SetStateAction<boolean>>)}
        >
          {description}
        </VtxCheckbox>
      </div>
    </div>
  );
}

const renderRadioOptions = (options: RadioOption[]): JSX.Element[] => {
  Logging.Debug('Render Radio Options', options);

  return options.map((option: RadioOption) => { // eslint-disable-line arrow-body-style
    return (
      <VtxRadio key={option.data} value={option.data}>{option.display}</VtxRadio>
    );
  });
};

// Radio Value has Changed
function onChangeRadio(
  event: any, // eslint-disable-line @typescript-eslint/no-explicit-any
  onChangeValue?: React.Dispatch<React.SetStateAction<number>>,
): void {
  if (onChangeValue) {
    const { value } = event.target;
    Logging.Debug('onChangeRadio', event.target.id, value);
    onChangeValue(value);
  }
}

// Render a Radio Group
export function renderRadioGroup(
  size: number,
  id: string,
  heading: string,
  options: RadioOption[],
  direction: 'horizontal' | 'vertical',
  value: number,
  onChangeValue: React.Dispatch<React.SetStateAction<number>>,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
  controlValidation: ControlValidation = null,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {
          heading
          && (
            <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
          )
        }
        <VtxRadioGroup
          id={id}
          name={id}
          value={value}
          disabled={controlState.disabled}
          direction={direction}
          onChange={(event): void => onChangeRadio(event, onChangeValue)}
        >
          {renderRadioOptions(options)}
        </VtxRadioGroup>
        {renderFieldValidation(id, value, controlState, controlValidation, style)}
      </div>
    </div>
  );
}

// TextArea Value has Changed
function onChangeTextArea(
  event: React.ChangeEvent<HTMLTextAreaElement>,
  onChangeValue?: React.Dispatch<React.SetStateAction<string>>,
): void {
  if (onChangeValue) {
    Logging.Debug('onChangeTextArea', event.target.id, event.target.value);
    onChangeValue(event.target.value);
  }
}

// Render an TextArea Field
export function renderTextArea(
  id: string,
  heading: string,
  value: string,
  placeholder: string,
  rows: number,
  onChangeValue: React.Dispatch<React.SetStateAction<string>>,
  controlState: ControlState,
  cssClass?: string,
): JSX.Element {
  return (
    <div className={cssClass}>
      {
        heading
        && (
          <label htmlFor={id}>{heading}</label>
        )
      }
      <VtxInputTextArea
        id={id}
        name={id}
        readOnly={controlState.readOnly}
        disabled={controlState.disabled}
        value={value || ''}
        placeholder={placeholder}
        rows={rows}
        onChange={(event): void => onChangeTextArea(event, onChangeValue)}
      />
    </div>
  );
}

// Edit Value has Changed
function onChangeEdit(
  event: React.ChangeEvent<HTMLInputElement>,
  onChangeValue?: React.Dispatch<React.SetStateAction<string>>,
): void {
  if (onChangeValue) {
    Logging.Debug('onChangeEdit', event.target.id, event.target.value, event.target.type);
    onChangeValue(event.target.value);
  }
}

// Render an Edit Field
export function renderEdit(
  size: number,
  id: string,
  heading: string,
  type: 'number' | 'text' | 'tel' | 'email',
  placeholder: string,
  value: string,
  onChangeValue: React.Dispatch<React.SetStateAction<string>>,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
  controlValidation: ControlValidation = null,
  prefix: React.ReactNode = null,
  suffix: React.ReactNode = null,
  addonBefore: string = null,
  addonAfter: string = null,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {
          heading
          && (
            <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
          )
        }
        <VtxInput
          type={type}
          id={id}
          name={id}
          prefix={prefix}
          suffix={suffix}
          readOnly={controlState.readOnly}
          disabled={controlState.disabled}
          placeholder={controlState.disabled || controlState.readOnly ? null : placeholder}
          value={value || ''}
          onChange={(event): void => onChangeEdit(event, onChangeValue)}
          addonBefore={addonBefore}
          addonAfter={addonAfter}
        />
        {renderFieldValidation(id, `${addonBefore || ''}${value || ''}${addonAfter || ''}`, controlState, controlValidation, style)}
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onChangeDate: any = (
  id: string,
  newDate: JSJoda.LocalDate,
  onChangeValue?: React.Dispatch<React.SetStateAction<Date>>,
): void => {
  if (onChangeValue) {
    if (newDate) {
      // Convert from a JSJoda LocalDate to native Javascript Date
      const value: Date = jsJodaLocalDateToJavascriptDate(newDate);
      Logging.Debug('onChangeDate', id, value);

      onChangeValue(value);
    } else {
      onChangeValue(null);
    }
  }
};

// Convert the Date Value to a Date Value if it is a String Value
const convertToDateIfString = (date: Date): Date => {
  if (date) {
    if (typeof date === 'object') {
      return date;
    }

    return new Date(date);
  }

  return null;
};

export function renderDate(
  size: number,
  id: string,
  heading: string,
  placeholder: string,
  value: Date,
  onChangeValue: React.Dispatch<React.SetStateAction<Date>>,
  disabledDate: (date: Date) => boolean,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
  controlValidation: ControlValidation = null,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  const checkDisabledDate = (date: JSJoda.LocalDate): boolean => {
    // Convert from a JSJoda LocalDate to native Javascript Date
    const javascriptDate: Date = jsJodaLocalDateToJavascriptDate(date);

    return disabledDate(javascriptDate);
  };

  if (controlState.editable) {
    // Convert the from a native Javascript Date to a JSJoda LocalDate
    const dateValue: JSJoda.LocalDate = value ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(value))) : null;

    return (
      <div className={className}>
        <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
          {
            heading
            && (
              <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
            )
          }
          <VtxDayPicker
            id={id}
            name={id}
            disabled={controlState.disabled}
            onChange={(day): void => onChangeDate(id, day, onChangeValue)}
            defaultValue={dateValue}
            placeholder={controlState.disabled || controlState.readOnly ? null : placeholder}
            className="fullWidthVtxDayPicker"
            disabledDate={disabledDate ? checkDisabledDate : null}
          />
          {renderFieldValidation(id, value, controlState, controlValidation, style)}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
        <input
          type="text"
          id={id}
          name={id}
          readOnly={controlState.readOnly}
          disabled={controlState.disabled}
          value={formatDateISO(convertToDateIfString(value)) || '--'}
        />
      </div>
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onChangeDates: any = (
  id: string,
  fromDate: JSJoda.LocalDate,
  toDate: JSJoda.LocalDate,
  onChangeValueFrom?: React.Dispatch<React.SetStateAction<Date>>,
  onChangeValueTo?: React.Dispatch<React.SetStateAction<Date>>,
): void => {
  if (onChangeValueFrom) {
    if (fromDate) {
      // Convert from a JSJoda LocalDate to native Javascript Date
      const value: Date = jsJodaLocalDateToJavascriptDate(fromDate);
      Logging.Debug('onChangeDate', id, value);

      onChangeValueFrom(value);
    } else {
      onChangeValueFrom(null);
    }
  }

  if (onChangeValueTo) {
    if (toDate) {
      // Convert from a JSJoda LocalDate to native Javascript Date
      const value: Date = jsJodaLocalDateToJavascriptDate(toDate);
      Logging.Debug('onChangeDate', id, value);

      onChangeValueTo(value);
    } else {
      onChangeValueFrom(null);
    }
  }
};

export function renderDateRange(
  size: number,
  id: string,
  heading: string,
  placeholder: string,
  valueFrom: Date,
  onChangeValueFrom: React.Dispatch<React.SetStateAction<Date>>,
  valueTo: Date,
  onChangeValueTo: React.Dispatch<React.SetStateAction<Date>>,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
  controlValidation: ControlValidation = null,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  // Convert the from a native Javascript Date to a JSJoda LocalDate
  const dateValueFrom: JSJoda.LocalDate = valueFrom ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(valueFrom))) : null;
  const dateValueTo: JSJoda.LocalDate = valueTo ? JSJoda.LocalDate.parse(formatDateISO(convertToDateIfString(valueTo))) : null;

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {
          heading
          && (
            <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
          )
        }
        <VtxDayRangePicker
          id={id}
          name={id}
          disabled={controlState.disabled}
          allowEmpty={[false, false]}
          placeholder={['From date', 'To date']}
          onChange={(days): void => onChangeDates(id, days.startDay, days.endDay, onChangeValueFrom, onChangeValueTo)}
          defaultValue={dateValueFrom && dateValueTo ? { startDay: dateValueFrom, endDay: dateValueTo } : undefined}
          className="fullWidthVtxDayPicker"
        />
        {renderFieldValidation(id, valueFrom || valueTo, controlState, controlValidation, style)}
      </div>
    </div>
  );
}

// Select Value has Changed
function onChangeSelect(
  id: string,
  value: string | string[],
  multiple: boolean,
  onChangeValue?: React.Dispatch<React.SetStateAction<string | string[]>>,
): void {
  if (onChangeValue) {
    Logging.Debug('onChangeSelect', value);

    onChangeValue(value);
  }
}

function renderSelectOptions(options: DisplayData[]): JSX.Element[] {
  const numberOptions = options.length;

  return options.map((option: DisplayData) => {
    const { data, display } = option;
    return (
      <VtxOption disabled={false} key={data} value={data}>{display}</VtxOption>
    );
  });
}

// Render an Select Field
export function renderSelect(
  size: number,
  id: string,
  heading: string,
  placeholder: string,
  multiple: boolean,
  required: boolean,
  options: DisplayData[],
  canSearch: boolean,
  searchAhead: SearchAhead = null,
  value: string | string[],
  onChangeValue: React.Dispatch<React.SetStateAction<string | string[]>>,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
  controlValidation: ControlValidation = null,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;
  const constructedOptions: DisplayData[] = [];

  // Options not specified.  Build it from the value
  if (!options || options.length === 0) {
    if (isString(value)) {
      constructedOptions.push({ data: value as string, display: value as string });
    } else if (Array.isArray(value)) {
      (value as string[]).forEach((singleValue: string) => {
        constructedOptions.push({ data: singleValue, display: singleValue });
      });
    }
  }

  Logging.Debug('renderSelect', multiple, value, options);

  const emptyDisplayValue = '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emptyDataValue: any = null;

  const searchingContent: JSX.Element = (
    <div className="searching">
      <img src="/images/loading-animation.svg" alt="loading" className="searchingImage" /><span>{searchAhead?.searchingText }</span>
    </div>
  );

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {
          heading
          && (
            <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
          )
        }
        {
          multiple
            ? (
              <VtxMultiSelect
                id={id}
                showSearch={canSearch}
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                disabled={controlState.disabled}
                placeholder={controlState.disabled || controlState.readOnly ? null : placeholder}
                value={value === null ? undefined : value as string[]}
                size="small"
                onChange={(newValue: string[]): void => onChangeSelect(id, newValue, multiple, onChangeValue)}
                onSearch={searchAhead ? _.debounce(searchAhead.onSearch, searchAhead.debounceMilliseconds) : null}
                notFoundContent={searchAhead ? searchAhead.searching ? searchingContent : searchAhead.noResultsText : null}
                dropdownClassName={style.fieldDropDown}
                mode="multiple"
              >
                {
                  !required
                  && (
                    <VtxOption value={emptyDataValue}>{emptyDisplayValue}</VtxOption>
                  )
                }
                {renderSelectOptions(!options || options.length === 0 ? constructedOptions : options)}
              </VtxMultiSelect>
            )
            : (
              <VtxSelect
                id={id}
                showSearch={canSearch}
                optionFilterProp="children"
                filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                disabled={controlState.disabled}
                placeholder={controlState.disabled || controlState.readOnly ? null : placeholder}
                value={value === null ? undefined : value as string}
                size="small"
                onChange={(newValue: string): void => onChangeSelect(id, newValue, multiple, onChangeValue)}
                onSearch={searchAhead ? _.debounce(searchAhead.onSearch, searchAhead.debounceMilliseconds) : null}
                notFoundContent={searchAhead ? searchAhead.searching ? searchingContent : searchAhead.noResultsText : null}
                dropdownClassName={style.fieldDropDown}
              >
                {
                  !required
                  && (
                    <VtxOption value={emptyDataValue}>{emptyDisplayValue}</VtxOption>
                  )
                }
                {renderSelectOptions(!options || options.length === 0 ? constructedOptions : options)}
              </VtxSelect>
            )
        }
        {renderFieldValidation(id, value, controlState, controlValidation, style, multiple)}
      </div>
    </div>
  );
}

// Render an Label Field
export function renderLabel(
  size: number,
  id: string,
  heading: string,
  controlState: ControlState,
  controlStyle: ControlStyleClass,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
      </div>
    </div>
  );
}

// Render an Links Field
export function renderLinks(
  size: number,
  id: string,
  heading: string,
  links: JSX.Element[],
  controlState: ControlState,
  controlStyle: ControlStyleClass,
): JSX.Element {
  const className = `field${size}`;
  const style: ControlStyleClass = controlStyle || defaultControlStyle;
  const classEnableDisable = controlState.editable ? style.fieldEnabled : style.fieldDisabled;

  return (
    <div className={className}>
      <div className={`form-item ${style.fieldBase} ${classEnableDisable}`}>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label className={style.fieldHeader} htmlFor={id}>{heading}</label>
        <div className="linksContainer">
          {links}
        </div>
      </div>
    </div>
  );
}

export function renderValidationMessage(id: string, validationMessage: string): JSX.Element {
  if (validationMessage) {
    return (
      <div className="defaultFieldValidationContainer">
        <span id={id} className="defaultFieldValidation">{validationMessage}</span>
      </div>
    );
  }

  return (
    <></>
  );
}
